import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import {
  MoveHorizontalIcon,
  ScanIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from 'lucide-react'

import { IUploadLog } from '~/client/graph'
import PdfDocument from '~/client/src/shared/components/PdfDocument/PdfDocument'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  getAttachmentFileIcon,
  getAttachmentFileType,
} from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'

// Zoom constants (same as FileFullscreenPreview)
const MAX_SCALE = 10
const MIN_SCALE = 0.05
const SCALE_STEP = 0.05

interface IExcelStatus {
  wordHtml: string
  isLoading: boolean
  error: string
}

interface IProps {
  attachment: IUploadLog
  fileName: string
  excelStatus: IExcelStatus
  excelContainerRef: React.RefObject<HTMLDivElement>
  onDownload: () => void
}

const PDF_HEIGHT_MARGIN = 20

const powerPointFilesCanBeViewedByDownloading =
  'PowerPoint files can be viewed by downloading'
const previewNotAvailableForThisFileType =
  'Preview not available for this file type'
const loadingWordDocument = 'Loading Word document...'
const loadingExcelDocument = 'Loading spreadsheet...'

// TODO: re-enable once the Excel preview bug is fixed
const ENABLE_EXCEL_PREVIEW = false

const AttachmentModalContent: React.FC<IProps> = ({
  attachment,
  fileName,
  excelStatus,
  excelContainerRef,
  onDownload,
}) => {
  // Zoom state management
  const [scale, setScale] = useState(1)
  const [shouldFitToWidth, setShouldFitToWidth] = useState(false)

  useEffect(() => {
    setScale(1)
    setShouldFitToWidth(false)
  }, [attachment.fileUrl])

  // Zoom functions
  const scaleIn = useCallback(() => {
    setScale(currentScale => {
      const newScale = currentScale + SCALE_STEP
      return newScale > MAX_SCALE ? MAX_SCALE : newScale
    })
  }, [])

  const scaleOut = useCallback(() => {
    setScale(currentScale => {
      const newScale = currentScale - SCALE_STEP
      return newScale < MIN_SCALE ? MIN_SCALE : newScale
    })
  }, [])

  const scaleToFit = useCallback(() => {
    setScale(1) // Reset to fit
    setShouldFitToWidth(current => !current) // Toggle fit to width
  }, [])

  // Render zoom controls for PDF
  const renderZoomControls = useCallback(() => {
    return (
      <div className="bottom-panel-holder full-width">
        <div className="row x-center">
          <div className="bottom-menu row no-grow pa2 bg-dark">
            <span
              className="bottom-toolbar-icon pa10 no-grow pointer"
              onClick={scaleIn}
            >
              <ZoomInIcon size={16} />
            </span>
            <span
              className="bottom-toolbar-icon pa10 no-grow pointer"
              onClick={scaleToFit}
            >
              {shouldFitToWidth ? (
                <ScanIcon size={16} />
              ) : (
                <MoveHorizontalIcon size={16} />
              )}
            </span>
            <span
              className="bottom-toolbar-icon pa10 no-grow pointer"
              onClick={scaleOut}
            >
              <ZoomOutIcon size={16} />
            </span>
          </div>
        </div>
      </div>
    )
  }, [scaleIn, scaleOut, scaleToFit, shouldFitToWidth])

  const renderPreviewContent = (): JSX.Element => {
    const { isLoading, error, wordHtml } = excelStatus
    const fileUrl = attachment.fileUrl
    const fileTypeEnum = getAttachmentFileType(attachment.fileType, '/')

    if (fileTypeEnum === FileType.Image) {
      return (
        <img
          src={fileUrl}
          alt="Attachment preview"
          className="full-width full-height object-fit-contain"
        />
      )
    }

    if (fileTypeEnum === FileType.Pdf) {
      return (
        <>
          <div className="full-height full-width overflow-hidden">
            <div className="full-height full-width">
              <PdfDocument
                className="full-height full-width overflow-auto"
                pageClassName="attachment-modal-pdf-page"
                file={fileUrl}
                disableTopMargin
                heightMargin={PDF_HEIGHT_MARGIN}
                widthMargin={PDF_HEIGHT_MARGIN}
                shouldRenderAllPages
                scale={scale}
                shouldFitToWidth={shouldFitToWidth}
              />
            </div>
          </div>
          {renderZoomControls()}
        </>
      )
    }

    if (fileTypeEnum === FileType.Word) {
      if (isLoading) {
        return (
          <div className="col y-center x-center p40">
            <div className="text large">{loadingWordDocument}</div>
          </div>
        )
      }

      if (error) {
        return (
          <div className="col y-center x-center p40">
            <div className="text large text-red mb16">{error}</div>
            <Button variant="default" onClick={onDownload}>
              {Localization.translator.download}
            </Button>
          </div>
        )
      }

      if (wordHtml) {
        return (
          <div
            className="full-width full-height bg-white overflow-auto pa20"
            dangerouslySetInnerHTML={{ __html: wordHtml }}
          />
        )
      }
    }

    if (ENABLE_EXCEL_PREVIEW && fileTypeEnum === FileType.Excel) {
      if (error) {
        return (
          <div className="col y-center x-center p40">
            <div className="text large text-red mb16">{error}</div>
            <Button variant="default" onClick={onDownload}>
              {Localization.translator.download}
            </Button>
          </div>
        )
      }

      return (
        <div className="full-width full-height relative">
          {isLoading && (
            <div className="absolute full-width full-height col y-center x-center bg-white">
              <div className="text large">{loadingExcelDocument}</div>
            </div>
          )}
          <div ref={excelContainerRef} className="full-width full-height" />
        </div>
      )
    }

    if (fileTypeEnum === FileType.PowerPoint) {
      return (
        <div className="col y-center x-center p40">
          {getAttachmentFileIcon(fileTypeEnum, 'mb16 h32 w32')}
          <div className="text large bold mb8">{fileName}</div>
          <div className="text medium text-grey-dark mb16 center">
            {powerPointFilesCanBeViewedByDownloading}
          </div>
        </div>
      )
    }

    return (
      <div className="col y-center x-center p40">
        {getAttachmentFileIcon(fileTypeEnum, 'mb16 h32 w32')}
        <div className="text large bold mb8">{fileName}</div>
        <div className="text medium text-grey-dark mb16">
          {previewNotAvailableForThisFileType}
        </div>
        <Button variant="default" onClick={onDownload}>
          {Localization.translator.download}
        </Button>
      </div>
    )
  }

  return (
    <div className="attachment-modal-preview-content flex-grow row x-center y-center p20 full-height">
      {renderPreviewContent()}
    </div>
  )
}

export default AttachmentModalContent
